import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Building2, 
  Calendar, 
  Users, 
  Briefcase, 
  DollarSign, 
  MapPin, 
  GraduationCap, 
  Languages, 
  Clock, 
  Tag, 
  User,
  Star,
  TrendingUp,
  Globe,
  Award,
  Target,
  Zap,
  Check,
  X,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getFormattedDateTime } from "@/utils/settings";
import { useSettings } from "@/providers/SettingsProvider";
import { useTranslation } from "react-i18next";
import { useJobLanguages } from "@/hooks/languages/useJobLanguages";
import { CEFR_LEVELS, COMMON_LANGUAGES } from "@/types/candidate";

interface JobOverviewCardProps {
  jobId?: string;
  company: {
    id: string;
    name: string;
  };
  formattedDate: string;
  matchesCount: number;
  skills: string[];
  
  // Contact information
  contact?: {
    id: string;
    first_name?: string;
    last_name?: string;
    name?: string;
  };
  
  // Position details
  seniorityLevel?: string;
  employmentType?: string;
  contractDuration?: string;
  employmentClassification?: string;
  
  // Timeline
  startDate?: string;
  applicationDeadline?: string;
  jobPostingDate?: string;
  
  // Compensation
  budget?: string;
  budgetFreelance?: string;
  
  // Location
  locationType?: string;
  workplaceBreakdown?: string;
  
  // Skills
  hardSkills?: string[];
  softSkills?: string[];
  
  // Language
  languageRequirements?: string | string[];
  extraLang?: string;
  
  // Additional
  educationLevel?: string;
  yearsOfExperience?: string;
  cultureTags?: string[];
  source?: string;
  assignedRecruiter?: {
    id: string;
    name: string;
    first_name?: string;
    last_name?: string;
  };
  
  // Custom fields
  custom_fields?: Record<string, any>;
  customFieldsConfig?: any[]; // Custom fields configuration for the organization
  
  // Closing reason
  closing_reason?: string;
  
  // Layout option
  horizontal?: boolean;
}

export const JobOverviewCard = ({ 
  jobId,
  company, 
  formattedDate, 
  matchesCount, 
  skills,
  contact,
  seniorityLevel,
  employmentType,
  contractDuration,
  employmentClassification,
  startDate,
  applicationDeadline,
  jobPostingDate,
  budget,
  budgetFreelance,
  locationType,
  workplaceBreakdown,
  hardSkills,
  softSkills,
  languageRequirements,
  extraLang,
  educationLevel,
  yearsOfExperience,
  cultureTags,
  source,
  assignedRecruiter,
  custom_fields,
  customFieldsConfig = [],
  closing_reason,
  horizontal = false
}: JobOverviewCardProps) => {
  const { dateFormat, timeFormat } = useSettings();
  const { t } = useTranslation();
  
  // Fetch job languages from database
  const { languages: jobLanguages, loading: languagesLoading } = useJobLanguages(jobId || '');
  
  // Initialize state from localStorage or default to true
  const [isCardsExpanded, setIsCardsExpanded] = useState(() => {
    const saved = localStorage.getItem('jobDetailsExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Save to localStorage whenever the state changes
  useEffect(() => {
    localStorage.setItem('jobDetailsExpanded', JSON.stringify(isCardsExpanded));
  }, [isCardsExpanded]);
  

  
  // Helper function to check if a section has data to display
  const hasData = (values: (string | string[] | undefined)[]) => {
    return values.some(value => {
      if (Array.isArray(value)) return value && value.length > 0;
      return !!value;
    });
  };

  // Helper to check if custom fields have values (only for currently configured fields)
  const hasCustomFields = (fields?: Record<string, any>) => {
    if (!fields || !customFieldsConfig.length) return false;
    
    // Only check fields that are currently configured in the admin panel
    return customFieldsConfig.some(fieldConfig => {
      const fieldValue = fields[fieldConfig.name];
      if (fieldConfig.type === 'boolean') {
        // For boolean fields, always show if they have a value (true or false)
        return fieldValue !== undefined;
      }
      // For other fields, check if they have a non-empty value
      if (Array.isArray(fieldValue)) return fieldValue && fieldValue.length > 0;
      return !!fieldValue;
    });
  };

  // Format dates according to user settings
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return getFormattedDateTime(new Date(dateString), dateFormat, timeFormat, false);
  };

  // Helper to translate education level
  const translateEducationLevel = (level?: string): string => {
    if (!level) return '';
    
    const educationLevelMap: Record<string, string> = {
      'HIGH_SCHOOL': 'highSchool',
      'ASSOCIATE': 'associate',
      'BACHELOR': 'bachelor',
      'MASTER': 'master',
      'DOCTORATE': 'doctorate',
      'NOT_REQUIRED': 'notRequired'
    };
    
    const i18nKey = educationLevelMap[level] || level.toLowerCase();
    return t(`jobs.formWizard.educationLevels.${i18nKey}`);
  };

  // Helper to format custom field values
  const formatCustomFieldValue = (fieldName: string, fieldValue: any) => {
    // Check if this is a boolean field by looking at the custom fields configuration
    const fieldConfig = customFieldsConfig.find(field => field.name === fieldName);
    const isBooleanField = fieldConfig?.type === 'boolean';
    
    if (isBooleanField) {
      return fieldValue ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-red-600" />
      );
    }
    
    // For non-boolean fields, return the value as before
    return Array.isArray(fieldValue) ? fieldValue.join(', ') : String(fieldValue);
  };

  // Check which sections have data
  const hasPositionDetails = hasData([seniorityLevel, employmentType, contractDuration, employmentClassification]);
  const hasTimeline = hasData([startDate, applicationDeadline, jobPostingDate]);
  const hasCompensation = hasData([budget, budgetFreelance]);
  const hasLocation = hasData([locationType, workplaceBreakdown]);
  const hasSkills = hasData([skills, hardSkills, softSkills]);
  const hasLanguage = jobLanguages.length > 0;
  const hasAdditional = hasData([educationLevel, yearsOfExperience, cultureTags, source, assignedRecruiter?.name]);
  const hasClosingReason = hasData([closing_reason]);
  const hasCustomFieldData = hasCustomFields(custom_fields);
  
  // Check if custom fields are configured for the organization (same logic as custom fields tab)
  const hasCustomFieldsConfigured = customFieldsConfig.length > 0;



  // If in horizontal mode, render a modern card-based layout
  if (horizontal) {
    return (
      <div className={isCardsExpanded ? "space-y-6" : "space-y-0"}>
        {/* Collapsible Header */}
        <div className={`flex items-center justify-between transition-all duration-300 ${
          isCardsExpanded 
            ? "py-4 px-0" 
            : "py-0.5 px-1 bg-white dark:bg-gray-800 rounded"
        }`}>
          <button
            onClick={() => setIsCardsExpanded(!isCardsExpanded)}
            className={`flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 transition-colors ${
              isCardsExpanded ? "text-lg" : "text-sm"
            }`}
          >
            {t("jobs.overview.details", "Job Details")}
            {isCardsExpanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          
          <div className="flex items-center gap-3">
            {source && (
              <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                <Tag className="h-3 w-3" />
                <span>{source}</span>
              </div>
            )}
            
            {isCardsExpanded && (
              <button
                onClick={() => setIsCardsExpanded(!isCardsExpanded)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-sm transition-colors"
              >
                <ChevronUp className="h-4 w-4" />
                {t("common.collapse", "Collapse")}
              </button>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isCardsExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* Column 1: General Information */}
            <div className="space-y-4">
              <div className="bg-blue-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <div className="p-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-sm mr-2">
                    <Building2 className="h-4 w-4 text-blue-500" />
                  </div>
                  {t("jobs.formWizard.general", "General")}
                </h3>
              <div className="space-y-2">
                <div>
                  <Link to={`/companies/${company.id}`} className="text-base font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-500 transition-colors">
                    {company.name}
                  </Link>
                </div>
                
                {contact && (contact.name || (contact.first_name && contact.last_name)) && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.contactPerson", "Contact")}</span>
                    <span className="text-sm font-medium">
                      {contact.name || `${contact.first_name} ${contact.last_name}`}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.jobPostedOn", "Posted")}</span>
                  <span className="text-sm font-medium">{formattedDate}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.candidates", "Candidates")}</span>
                  <span className="text-sm font-medium">{matchesCount}</span>
                </div>
              </div>
            </div>

            {hasPositionDetails && (
              <div className="bg-blue-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <Briefcase className="h-4 w-4 mr-2 text-blue-500" />
                  {t("jobs.formWizard.positionDetails", "Position Details")}
                </h3>
                <div className="space-y-2">
                  {seniorityLevel && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.seniorityLevel", "Seniority")}</span>
                      <Badge variant="outline" className="text-xs">
                        <Star className="h-3 w-3 mr-1" />
                        {seniorityLevel}
                      </Badge>
                    </div>
                  )}

                  {employmentType && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.employmentType", "Type")}</span>
                      <Badge variant="outline" className="text-xs">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        {employmentType}
                      </Badge>
                    </div>
                  )}
                  {contractDuration && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.contractDuration", "Duration")}</span>
                      <span className="text-sm font-medium">{contractDuration}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Column 2: Timeline & Compensation */}
          <div className="space-y-4">
            {hasTimeline && (
              <div className="bg-green-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <Clock className="h-4 w-4 mr-2 text-green-600" />
                  {t("jobs.formWizard.timeline", "Timeline")}
                </h3>
                <div className="space-y-2">
                  {startDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.startDate", "Start")}</span>
                      <span className="text-sm font-medium">{formatDate(startDate)}</span>
                    </div>
                  )}
                  {applicationDeadline && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.applicationDeadline", "Deadline")}</span>
                      <span className="text-sm font-medium">{formatDate(applicationDeadline)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {hasCompensation && (
              <div className="bg-green-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <DollarSign className="h-4 w-4 mr-2 text-green-600" />
                  {t("jobs.formWizard.compensation", "Compensation")}
                </h3>
                <div className="space-y-2">
                  {budget && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.budgetPermanent", "Permanent")}</span>
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">{budget}</span>
                    </div>
                  )}
                  {budgetFreelance && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.budgetFreelance", "Freelance")}</span>
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">{budgetFreelance}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {hasLocation && (
              <div className="bg-purple-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <MapPin className="h-4 w-4 mr-2 text-purple-600" />
                  {t("jobs.formWizard.location", "Location")}
                </h3>
                <div className="space-y-2">
                  {locationType && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.locationType", "Type")}</span>
                      <Badge variant="outline" className="text-xs">
                        <Globe className="h-3 w-3 mr-1" />
                        {locationType}
                      </Badge>
                    </div>
                  )}
                  {workplaceBreakdown && locationType === "Hybrid" && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.breakdown", "Breakdown")}</span>
                      <span className="text-sm font-medium">{workplaceBreakdown}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Column 3: Requirements & Languages */}
          <div className="space-y-4">
            {hasAdditional && (
              <div className="bg-indigo-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <Award className="h-4 w-4 mr-2 text-indigo-600" />
                  {t("jobs.formWizard.additionalRequirements", "Requirements")}
                </h3>
                <div className="space-y-2">
                  {educationLevel && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.educationLevel", "Education")}</span>
                      <span className="text-sm font-medium">{translateEducationLevel(educationLevel)}</span>
                    </div>
                  )}
                  {yearsOfExperience && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.experienceRequired", "Experience")}</span>
                      <span className="text-sm font-medium">{yearsOfExperience}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {hasLanguage && (
              <div className="bg-orange-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <Languages className="h-4 w-4 mr-2 text-orange-600" />
                  {t("jobs.formWizard.languageRequirements", "Languages")}
                </h3>
                {languagesLoading ? (
                  <div className="text-xs text-gray-500">{t("common.loading", "Loading...")}</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {jobLanguages.map((lang) => {
                      const languageName = COMMON_LANGUAGES[lang.language_code] || lang.language;
                      const levelLabel = CEFR_LEVELS[lang.min_cefr_level];
                      
                      return (
                        <Badge 
                          key={lang.id} 
                          variant={lang.is_mandatory ? "default" : "secondary"}
                          className="flex items-center gap-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300"
                        >
                          <Languages className="h-3 w-3" />
                          {languageName} ({levelLabel})
                          {lang.is_mandatory && " *"}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {hasCustomFieldsConfigured && hasCustomFieldData && (
              <div className="bg-red-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <Tag className="h-4 w-4 mr-2 text-red-600" />
                  {t("jobs.customFields.title", "Custom Fields")}
                </h3>
                <div className="space-y-2">
                  {customFieldsConfig.map((fieldConfig) => {
                    const fieldValue = custom_fields?.[fieldConfig.name];
                    
                    // For boolean fields, show even if false. For other fields, skip if empty/falsy
                    if (fieldConfig.type !== 'boolean' && !fieldValue) return null;
                    
                    return (
                      <div key={fieldConfig.name} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{fieldConfig.name}</span>
                        <div className="text-sm font-medium">
                          {formatCustomFieldValue(fieldConfig.name, fieldValue)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Column 4: Skills & Expertise */}
          <div className="space-y-4">
            {hasSkills && (
              <div className="bg-yellow-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <Zap className="h-4 w-4 mr-2 text-yellow-600" />
                  {t("jobs.formWizard.keySkills", "Skills & Expertise")}
                </h3>
                <div className="space-y-3">
                  {skills && skills.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                        {t("jobs.formWizard.skills", "General Skills")}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {skills.map((skill, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {hardSkills && hardSkills.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                        {t("jobs.formWizard.hardSkills", "Hard Skills")}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {hardSkills.map((skill, index) => (
                          <Badge key={index} variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-300 border-blue-200 dark:border-blue-700">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {softSkills && softSkills.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                        {t("jobs.formWizard.softSkills", "Soft Skills")}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {softSkills.map((skill, index) => (
                          <Badge key={index} variant="outline" className="text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {cultureTags && cultureTags.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                        {t("jobs.formWizard.cultureTags", "Culture")}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {cultureTags.map((tag, index) => (
                          <Badge key={index} variant="outline" className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assigned Recruiter Card */}
            {assignedRecruiter && (
              <div className="bg-gray-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <User className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400" />
                  {t("jobs.formWizard.assignedRecruiter", "Assigned Recruiter")}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.recruiter", "Recruiter")}</span>
                    <span className="text-sm font-medium">{assignedRecruiter.name}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Closing Reason Card */}
            {hasClosingReason && (
              <div className="bg-red-50/30 dark:bg-gray-900 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold flex items-center mb-3">
                  <X className="h-4 w-4 mr-2 text-red-600" />
                  {t("jobs.formWizard.closingReason", "Closing Reason")}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t("jobs.formWizard.reason", "Reason")}</span>
                    <span className="text-sm font-medium text-red-700 dark:text-red-400">{closing_reason}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>

      </div>
    );
  }
  
  // Original vertical layout (simplified for backward compatibility)
  return (
    <Card className="bg-white dark:bg-gray-800 shadow mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{t("jobs.overview.title", "Job Overview")}</CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Company, Posted date and Matches */}
        <div className="space-y-2 pb-3 border-b">
          <div className="flex items-center">
            <Building2 className="h-4 w-4 text-gray-400 mr-2" />
            <Link to={`/companies/${company.id}`} className="text-digger-blue hover:underline text-sm">
              {company.name}
            </Link>
          </div>
          
          <div className="flex items-center">
            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-sm">{formattedDate}</span>
          </div>
          
          <div className="flex items-center">
            <Users className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-sm">{matchesCount} {t("jobs.formWizard.candidates", "candidates")}</span>
          </div>
        </div>
        
        {/* Position Details */}
        {hasPositionDetails && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.positionDetails", "Position Details")}</h3>
            
            {seniorityLevel && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.seniorityLevel", "Seniority Level")}</span>
                <span className="text-sm">{seniorityLevel}</span>
              </div>
            )}
            
            {employmentType && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.employmentType", "Employment Type")}</span>
                <span className="text-sm">{employmentType}</span>
              </div>
            )}
            
            {contractDuration && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.contractDuration", "Contract Duration")}</span>
                <span className="text-sm">{contractDuration}</span>
              </div>
            )}
            
            {employmentClassification && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.timeCommitment", "Time Commitment")}</span>
                <span className="text-sm">{employmentClassification}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Timeline */}
        {hasTimeline && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.timeline", "Timeline")}</h3>
            
            {startDate && (
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.startDate", "Start Date")}</span>
                <span className="text-sm">{formatDate(startDate)}</span>
              </div>
            )}
            
            {applicationDeadline && (
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.applicationDeadline", "Application Deadline")}</span>
                <span className="text-sm">{formatDate(applicationDeadline)}</span>
              </div>
            )}
            
            {jobPostingDate && (
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.jobPostedOn", "Job Posted On")}</span>
                <span className="text-sm">{formatDate(jobPostingDate)}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Compensation */}
        {hasCompensation && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.compensation", "Compensation")}</h3>
            
            {budget && (
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.budgetPermanent", "Budget (Permanent)")}</span>
                <span className="text-sm">{budget}</span>
              </div>
            )}
            
            {budgetFreelance && (
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.budgetFreelance", "Budget (Freelance)")}</span>
                <span className="text-sm">{budgetFreelance}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Location */}
        {hasLocation && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.location", "Location")}</h3>
            
            {locationType && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.locationType", "Location Type")}</span>
                <span className="text-sm">{locationType}</span>
              </div>
            )}
            
            {workplaceBreakdown && locationType === "Hybrid" && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.breakdown", "Breakdown")}</span>
                <span className="text-sm">{workplaceBreakdown}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Skills */}
        {hasSkills && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.keySkills", "Skills & Expertise")}</h3>
            
            {skills && skills.length > 0 && (
              <div className="mb-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">{t("jobs.formWizard.skills", "Skills")}</span>
                <div className="flex flex-wrap gap-1">
                  {skills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {hardSkills && hardSkills.length > 0 && (
              <div className="mb-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">{t("jobs.formWizard.hardSkills", "Hard Skills")}</span>
                <div className="flex flex-wrap gap-1">
                  {hardSkills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {softSkills && softSkills.length > 0 && (
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">{t("jobs.formWizard.softSkills", "Soft Skills")}</span>
                <div className="flex flex-wrap gap-1">
                  {softSkills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Language Requirements */}
        {hasLanguage && (
          <div className="space-y-2 pb-3 border-b">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.languageRequirements", "Language Requirements")}</h3>
            
            {languagesLoading ? (
              <div className="text-xs text-gray-500">{t("common.loading", "Loading...")}</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {jobLanguages.map((lang) => {
                  const languageName = COMMON_LANGUAGES[lang.language_code] || lang.language;
                  const levelLabel = CEFR_LEVELS[lang.min_cefr_level];
                  
                  return (
                    <Badge 
                      key={lang.id} 
                      variant={lang.is_mandatory ? "default" : "secondary"}
                      className="flex items-center gap-1"
                    >
                      <Languages className="h-3 w-3" />
                      {languageName} ({levelLabel})
                      {lang.is_mandatory && " *"}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Additional Requirements */}
        {hasAdditional && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t("jobs.formWizard.additionalRequirements", "Additional Requirements")}</h3>
            
            {educationLevel && (
              <div className="flex items-center">
                <GraduationCap className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.educationLevel", "Education Level")}</span>
                <span className="text-sm">{translateEducationLevel(educationLevel)}</span>
              </div>
            )}
            
            {yearsOfExperience && (
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.experienceRequired", "Experience Required")}</span>
                <span className="text-sm">{yearsOfExperience}</span>
              </div>
            )}
            
            {cultureTags && cultureTags.length > 0 && (
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">{t("jobs.formWizard.cultureTags", "Culture Tags")}</span>
                <div className="flex flex-wrap gap-1">
                  {cultureTags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {source && (
              <div className="flex items-center">
                <Tag className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.source", "Source")}</span>
                <span className="text-sm">{source}</span>
              </div>
            )}
            
            {assignedRecruiter && (
              <div className="flex items-center">
                <User className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{t("jobs.formWizard.assignedRecruiter", "Assigned Recruiter")}</span>
                <span className="text-sm">{assignedRecruiter.name}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Custom Fields */}
        {hasCustomFieldsConfigured && hasCustomFieldData && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t("jobs.customFields.title", "Custom Fields")}</h3>
            {customFieldsConfig.map((fieldConfig) => {
              const fieldValue = custom_fields?.[fieldConfig.name];
              
              // For boolean fields, show even if false. For other fields, skip if empty/falsy
              if (fieldConfig.type !== 'boolean' && !fieldValue) return null;
              
              return (
                <div key={fieldConfig.name} className="flex items-center">
                  <Tag className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-xs text-gray-500 dark:text-gray-400 w-24">{fieldConfig.name}</span>
                  <div className="text-sm">
                    {formatCustomFieldValue(fieldConfig.name, fieldValue)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
